import React, { useState } from "react";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import Modal from "../../../../components/base/Modal";
import Icon from "../../../../components/base/Icon";
import { toShortDateWithoutTime, useHub } from "../../../../core/Global";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import AddonCatalog, { AddonPurchase, AddonState } from "../models/AddonCatalog";
import "./AddonsCard.css";


// Everything about one add-on on the wall: when it was bought, when it last renewed, when it
// renews next, and what can be done about it.
//
// Cancelling never refunds and never stops anything today: the add-on runs to the end of what was
// paid for and is simply not renewed. What the plan includes has no cancel at all -- it comes and
// goes with the plan. Extra users can also be lowered in number, from the next period.
//
// Drawn from the wall's own data rather than a copy, so after an action the page re-reads the
// add-ons and this drawer shows the new state without closing.
export default function AddonDetailsDrawer(props: {
    item: AddonCatalog,
    purchase?: AddonPurchase,
    drawerRef?: React.RefObject<DrawerRef>,
    onClose: () => void,
    onChanged: () => void
}) {

    const hub = useHub()

    const [nextCount, nextCountSet] = useState<number>()
    const [saving, savingSet] = useState<boolean>(false)

    const item = props.item
    const purchase = props.purchase

    const name = purchase?.name ?? item.name
    const included = item.state === AddonState.Included
    const canceled = purchase ? purchase.isCanceled : item.isCanceled
    const canCancel = purchase ? purchase.canCancel : item.canCancel
    const addonId = purchase?.id ?? item.addonId
    const purchasedAt = purchase?.purchasedAt ?? item.purchasedAt
    const renewedAt = purchase?.renewedAt ?? item.renewedAt
    const expiresAt = purchase?.expiresAt ?? item.expiresAt
    const count = purchase?.count ?? 0

    // The number the next period will have, as chosen here or as already scheduled.
    const scheduled = purchase?.nextCount ?? count
    const chosen = nextCount ?? scheduled

    // Renewed at least once since it was bought. The two dates are a few seconds apart on the
    // day of purchase, which is not a renewal.
    const everRenewed = !!renewedAt && !!purchasedAt
        && Math.abs(new Date(renewedAt).getTime() - new Date(purchasedAt).getTime()) > 60 * 60 * 1000

    const date = (d?: string) => d ? toShortDateWithoutTime(new Date(d)) : "—"

    const post = (url: string, body: any, done: string, callback?: (keepOpen: boolean) => void) => {
        hub.Post(url, body, res => {
            if (res.success) { toastSuccess(done); props.onChanged() }
            else toastError(res.message)
            callback?.(!res.success)
        }, () => callback?.(true))
    }

    const saveCount = () => {
        savingSet(true)
        hub.Post("/accounts/profile/plan/extrausers/reduce", { id: addonId, count: chosen }, res => {
            savingSet(false)
            if (res.success) {
                toastSuccess(chosen === count
                    ? "The change has been taken back."
                    : `From ${date(expiresAt)} this purchase will have ${chosen} user${chosen === 1 ? "" : "s"}.`)
                nextCountSet(undefined)
                props.onChanged()
            }
            else toastError(res.message)
        }, () => savingSet(false))
    }

    const row = (label: string, value: React.ReactNode) => (
        <div className="addon-detail-row">
            <span className="addon-detail-label">{label}</span>
            <span className="addon-detail-value">{value}</span>
        </div>
    )

    return (
        <>
            <Drawer ref={props.drawerRef} id="drawer-addon-details"
                title={name}
                titleIcon={item.icon}
                subtitle={item.tagline}
                size="normal"
                onClose={props.onClose}>

                <div className="addon-detail">

                    <div className="mb-6">
                        {
                            included ?
                                <span className="badge badge-light-primary fw-bold px-3 py-2">
                                    <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                                    In your plan
                                </span>
                                : canceled ?
                                    <span className="badge badge-light-danger fw-bold px-3 py-2">
                                        <Icon icon="clock" type="solid" size={10} className="me-1" />
                                        Cancelled — ends {date(expiresAt)}
                                    </span>
                                    :
                                    <span className="badge badge-light-success fw-bold px-3 py-2">
                                        <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                                        Active
                                    </span>
                        }
                    </div>

                    {item.description && <p className="fs-6 text-gray-700 mb-8">{item.description}</p>}

                    <div className="addon-detail-list mb-8">
                        {row("Price", included ? "Included with your plan" : (purchase?.priceText ?? item.priceText))}
                        {
                            purchase &&
                            row("Users", purchase.nextCount
                                ? <>{count} now · <b>{purchase.nextCount}</b> from {date(expiresAt)}</>
                                : count)
                        }
                        {!included && row("Purchased", date(purchasedAt))}
                        {!included && row("Last renewed", everRenewed ? date(renewedAt) : "Not renewed yet")}
                        {
                            included
                                ? row("Renews", "With your plan")
                                : row(canceled ? "Ends on" : "Next renewal", date(expiresAt))
                        }
                    </div>

                    {
                        /* Fewer users from the next period. The period already paid for keeps all
                           of them; adding users is a new purchase, so the choice stops at today's. */
                        purchase && !canceled && count > 1 &&
                        <div className="mb-8">
                            <div className="fw-bold mb-2">Users from the next period</div>
                            <div className="fs-7 text-gray-600 mb-4">
                                Keeps {count} until {date(expiresAt)}. Nothing is refunded; the next
                                renewal charges for the number you choose.
                            </div>
                            <div className="extra-users-picker mb-4">
                                {
                                    Array.from({ length: count }, (_, i) => i + 1).map(n =>
                                        <button key={n} type="button"
                                            className={"extra-users-option" + (n === chosen ? " is-selected" : "")}
                                            onClick={e => { e.preventDefault(); nextCountSet(n); }}>
                                            {n}
                                        </button>
                                    )
                                }
                            </div>
                            <button type="button" className="btn btn-sm btn-primary"
                                disabled={saving || chosen === scheduled}
                                onClick={e => { e.preventDefault(); saveCount(); }}>
                                {
                                    saving ? "Saving..."
                                        : chosen === count ? `Keep ${count} users`
                                            : `Switch to ${chosen} user${chosen === 1 ? "" : "s"} from ${date(expiresAt)}`
                                }
                            </button>
                        </div>
                    }

                    {
                        included &&
                        <div className="addon-detail-note">
                            <Icon icon="circle-info" type="solid" size={12} className="me-2" />
                            Comes with your plan and renews with it, so it cannot be cancelled on its own.
                        </div>
                    }

                    {
                        !included && canCancel && !canceled &&
                        <button type="button" className="btn btn-sm btn-light-danger"
                            onClick={e => { e.preventDefault(); window.openModal("modal-cancel-plan-addon"); }}>
                            <Icon icon="ban" type="solid" size={11} className="me-2" />
                            Cancel {purchase ? "this purchase" : "add-on"}
                        </button>
                    }

                    {
                        !included && canceled &&
                        <button type="button" className="btn btn-sm btn-light-primary"
                            onClick={e => {
                                e.preventDefault();
                                post("/accounts/profile/plan/addon/resume", { id: addonId }, `${name} will renew as usual.`)
                            }}>
                            <Icon icon="rotate-left" type="solid" size={11} className="me-2" />
                            Keep it — undo the cancellation
                        </button>
                    }

                </div>
            </Drawer>

            <Modal id="modal-cancel-plan-addon"
                title={"Cancel " + name}
                buttonText="Cancel Add-on" buttonColor="danger"
                onConfirm={callback =>
                    post("/accounts/profile/plan/addon/cancel", { id: addonId },
                        `${name} is cancelled and ends on ${date(expiresAt)}.`, callback)}>
                <p className="mb-3">
                    {name} keeps working until <b>{date(expiresAt)}</b>, the end of the period you
                    have paid for. After that it is not renewed and comes off your account.
                </p>
                <p className="mb-0 text-gray-600">
                    Nothing is refunded. You can undo this any time before {date(expiresAt)}.
                </p>
            </Modal>
        </>
    )
}
